import apiClient from './config';

export interface PMTemplateSettings {
  kanbanEnabled: boolean;
  sprintEnabled: boolean;
  cycleEnabled: boolean;
  updatedAt?: string | null;
  updatedBy?: number | null;
}

// PM template settings rarely change (only when an admin flips a
// template on /admin/modules). Cache them in localStorage with a
// stale-while-revalidate pattern so the TaskManagerHub doesn't pay a
// round-trip on every PM page entry. The hub still re-fetches on
// mount to refresh, but consumers can show the cached value
// immediately.
const STORAGE_KEY = 'pm.templateSettings.v1';
const STALE_AFTER_MS = 5 * 60_000; // 5 min — stays fresh enough to react to admin toggles.

interface CacheEnvelope {
  at: number;
  data: PMTemplateSettings;
}

function readCache(): CacheEnvelope | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEnvelope;
    if (!parsed || typeof parsed.at !== 'number' || !parsed.data) return null;
    return parsed;
  } catch { return null; }
}

function writeCache(data: PMTemplateSettings) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch { /* storage full / disabled — ignore */ }
}

export const pmTemplatesAPI = {
  // Returns the cached value immediately if it's fresh, otherwise hits
  // the API. Use `getFresh()` when you specifically need an up-to-date
  // result (e.g. the admin settings page).
  get: async (): Promise<PMTemplateSettings> => {
    const cached = readCache();
    if (cached && (Date.now() - cached.at) < STALE_AFTER_MS) {
      return cached.data;
    }
    const r = await apiClient.get('/company/pm-templates');
    const data = r.data?.data as PMTemplateSettings;
    if (data) writeCache(data);
    return data;
  },
  // Synchronous peek for code paths that want to render instantly with
  // whatever's cached and let a background fetch update later.
  peek: (): PMTemplateSettings | null => readCache()?.data ?? null,
  getFresh: async (): Promise<PMTemplateSettings> => {
    const r = await apiClient.get('/company/pm-templates');
    const data = r.data?.data as PMTemplateSettings;
    if (data) writeCache(data);
    return data;
  },
  update: async (
    next: Partial<Pick<PMTemplateSettings, 'kanbanEnabled' | 'sprintEnabled' | 'cycleEnabled'>>,
  ): Promise<PMTemplateSettings> => {
    const r = await apiClient.put('/company/pm-templates', next);
    const data = r.data?.data as PMTemplateSettings;
    if (data) writeCache(data);
    return data;
  },
  // Drop the cached settings (called on logout to avoid leaking
  // another tenant's toggles into the next session).
  clearCache: () => {
    try { localStorage.removeItem(STORAGE_KEY); } catch { /* swallow */ }
  },
};

export default pmTemplatesAPI;
